"use client";

import { useEffect, useRef, type RefObject } from "react";

import {
  trackAgentRevenueOfferContractRejected,
  trackAgentRevenueOfferShown,
} from "@/lib/analytics-agent-offers";
import { offerPresentationId, type OfferActionMode } from "@/lib/analytics-offer-milestones";
import {
  ISHVARAM_COMMERCE_SYSTEM,
  ISHVARAM_REPORT_CHECKOUT_CHANNEL,
} from "@/lib/commerce-attribution";
import { useImpressionWhenVisible } from "@/hooks/useImpressionOnce";
import type { NextAction } from "@/lib/api/chat";
import { governedActionEventProperties } from "@/lib/governed-action";
import {
  deliverOfferImpression,
  preloadOfferImpressionEmitter,
} from "./OfferImpression.delivery-bridge";
import type { OfferImpressionEmission, OfferImpressionResolution } from "./OfferImpression.emit";

export interface OfferImpressionFields {
  checkoutChannel?: string;
  commerceSystem?: string;
  confidence?: number;
  customerProblem?: string;
  journeyAction?: NextAction | null;
  landingIntent?: string;
  landingPath?: string;
  /** The collect-inputs placement that sent the seeker to this offer. */
  originPlacement?: string;
  presentationKey?: string;
  offerType?: string;
  productName?: string;
  productSku?: string;
  rankedOfferOrder?: string[];
  routeRankReason?: string;
  routeRankScore?: number;
  strongestDoshaId?: string;
  strongestDoshaSeverity?: number;
  urgency?: string;
}

/**
 * OfferImpression — a near-render-nothing client island that fires the
 * `offer_seen` (`agent_revenue_offer_shown`) impression EXACTLY ONCE per offer
 * presentation, AND ONLY ONCE THE OFFER IS GENUINELY SEEN. Part of the
 * offer-placement architecture (ADR-0025).
 *
 * Why this exists: every report CTA needs the same mirrored `agent_revenue_*` +
 * `commerce_*` shown payload and the same re-fire protection. Server-rendered
 * offer surfaces (e.g. the collect-inputs OfferPlacement on muhurat / dasha /
 * sade-sati) also need a tiny client island so they can emit the event without
 * converting the whole surface to a client component. Re-fire-proof by
 * construction via {@link useImpressionWhenVisible} (gated on a stable string
 * key, never object identity — the 2026-06 re-fire incident class) and on real
 * VISIBILITY (a 0×0 sentinel observed by IntersectionObserver), so a below-the-
 * fold offer the seeker never scrolled to no longer inflates `offer_seen`.
 */
export interface OfferImpressionProps extends OfferImpressionFields {
  planId: string;
  actionMode: OfferActionMode;
  sourcePath: string;
  intent: string;
  placement: string;
  /**
   * When the offer's tappable CTA is lazy-loaded (`ssr:false` island), pass the
   * card's mounted state here so `offer_seen` is armed only once the button is
   * actually present. Omit (undefined) to arm immediately.
   */
  ready?: boolean;
  /** Actual painted offer root. Preferred over the legacy zero-size sentinel. */
  targetRef?: RefObject<Element | null>;
}

interface ResolvedOfferPresentation {
  planId: string;
  actionMode: OfferActionMode;
  sourcePath: string;
  intent: string;
  placement: string;
  presentationId: string;
}

export default function OfferImpression({
  planId,
  offerType = "report",
  productSku,
  productName,
  actionMode,
  sourcePath,
  landingPath,
  landingIntent,
  originPlacement,
  intent,
  placement,
  checkoutChannel = ISHVARAM_REPORT_CHECKOUT_CHANNEL,
  commerceSystem = ISHVARAM_COMMERCE_SYSTEM,
  customerProblem,
  urgency = "medium",
  confidence,
  routeRankScore,
  routeRankReason,
  rankedOfferOrder,
  presentationKey,
  journeyAction,
  strongestDoshaId,
  strongestDoshaSeverity,
  ready,
  targetRef,
}: OfferImpressionProps) {
  const sentinelRef = useRef<HTMLSpanElement | null>(null);
  const observedRef = targetRef ?? sentinelRef;
  useEffect(preloadOfferImpressionEmitter, []);
  const resolved = resolveOfferImpressionPresentation({
    planId,
    actionMode,
    sourcePath,
    intent,
    placement,
    presentationKey,
  });
  useImpressionWhenVisible(
    // Hold the key null until the CTA has mounted (ready !== false); a null key
    // makes useImpressionWhenVisible WAIT — it never drops the impression, it
    // just defers it to the moment the button exists (fires once, then).
    ready === false ? null : resolved.presentationId,
    () => {
      const emission: OfferImpressionEmission = {
        ...resolved,
        offerType,
        productSku,
        productName,
        checkoutChannel,
        commerceSystem,
        landingPath,
        landingIntent,
        originPlacement,
        customerProblem,
        urgency,
        confidence,
        routeRankScore,
        routeRankReason,
        rankedOfferOrder,
        journeyAction,
        strongestDoshaId,
        strongestDoshaSeverity,
      };
      return deliverOfferImpression(emission, (resolution: OfferImpressionResolution) => {
        if (!resolution.ok) {
          trackAgentRevenueOfferContractRejected({
            placement: emission.placement,
            plan_id: emission.planId,
            source_path: emission.sourcePath,
            intent: emission.intent,
            offer_action_mode: emission.actionMode,
            reason: resolution.reason,
          });
          return;
        }

        const resolved = resolution.value;
        trackAgentRevenueOfferShown({
          offer_type: resolved.offerType,
          product_sku: resolved.productSku,
          product_name: resolved.productName,
          offer_action_mode: resolved.actionMode,
          presentation_id: resolved.presentationId,
          plan_id: resolved.planId,
          source_path: resolved.sourcePath,
          landing_path: resolved.landingPath,
          landing_intent: resolved.landingIntent,
          origin_placement: resolved.originPlacement,
          intent: resolved.intent,
          placement: resolved.placement,
          checkout_channel: resolved.checkoutChannel,
          commerce_system: resolved.commerceSystem,
          customer_problem: resolved.customerProblem,
          urgency: resolved.urgency,
          confidence: resolved.confidence,
          route_rank_score: resolved.routeRankScore,
          route_rank_reason: resolved.routeRankReason,
          ranked_offer_order: resolved.rankedOfferOrder ?? [resolved.planId],
          ...governedActionEventProperties(resolved.journeyAction),
          strongest_dosha_id: resolved.strongestDoshaId,
          strongest_dosha_severity: resolved.strongestDoshaSeverity,
        });
      });
    },
    observedRef,
    // A sentinel point is 0-area, so gate on entering view (threshold 0), held
    // briefly so a fast scroll-past doesn't count.
    // persist: dedup across remounts within the session — the key carries
    // planId+placement+readingId+sourcePath, so it stays per-presentation unique
    // and can never re-inflate offer_seen on a chat/list re-render (rule 47).
    targetRef
      ? { threshold: 0.5, dwellMs: 1000, persist: true }
      : { threshold: 0, dwellMs: 800, persist: true }
  );
  // 0×0 invisible sentinel at the offer's location — the thing whose visibility
  // means "the offer scrolled into the seeker's view". aria-hidden + zero-box so
  // it never affects layout or a11y.
  return targetRef ? null : (
    <span ref={sentinelRef} aria-hidden="true" style={{ width: 0, height: 0 }} />
  );
}

function resolveOfferImpressionPresentation({
  planId,
  actionMode,
  sourcePath,
  intent,
  placement,
  presentationKey,
}: Pick<OfferImpressionProps, "planId" | "actionMode" | "sourcePath" | "intent" | "placement"> & {
  presentationKey?: string;
}): ResolvedOfferPresentation {
  const normalizedSourcePath = normalizePath(sourcePath);
  return {
    planId,
    actionMode,
    sourcePath: normalizedSourcePath,
    intent,
    placement,
    presentationId:
      presentationKey ??
      offerPresentationId({
        actionMode,
        planId,
        placement,
        sourcePath: normalizedSourcePath,
      }),
  };
}

function normalizePath(path: string): string {
  const clean = path.split(/[?#]/, 1)[0] || "/";
  const leading = clean.startsWith("/") ? clean : `/${clean}`;
  return leading === "/" ? leading : leading.replace(/\/+$/, "");
}
